/**
 * PresetsPanel - Save/load named configuration presets
 */

import { useState, useCallback } from 'react';
import { useConfigStore } from '@/store/configStore';
import {
  listPresets,
  savePreset,
  loadPreset,
  deletePreset,
  getConfigSnapshot,
} from '@/lib/configPresets';
import type { Preset } from '@/lib/configPresets';

export function PresetsPanel() {
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState<Preset[]>(() => listPresets());
  const [error, setError] = useState<string | null>(null);
  const [confirmLoad, setConfirmLoad] = useState<string | null>(null);

  const loadConfiguration = useConfigStore((s) => s.loadConfiguration);

  const refreshList = useCallback(() => {
    setPresets(listPresets());
  }, []);

  const handleSave = useCallback(() => {
    const name = presetName.trim();
    if (!name) return;
    setError(null);
    try {
      const state = useConfigStore.getState();
      const snapshot = getConfigSnapshot(state);
      savePreset(name, snapshot);
      setPresetName('');
      refreshList();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  }, [presetName, refreshList]);

  const handleLoad = useCallback((name: string) => {
    setError(null);
    const snapshot = loadPreset(name);
    if (!snapshot) {
      setError(`Preset "${name}" not found`);
      return;
    }
    loadConfiguration(snapshot);
    setConfirmLoad(null);
  }, [loadConfiguration]);

  const handleDelete = useCallback((name: string) => {
    setError(null);
    deletePreset(name);
    refreshList();
  }, [refreshList]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Save section */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <input
          type="text"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Preset name..."
          maxLength={40}
          style={{
            flex: 1,
            padding: '6px 8px',
            fontSize: '12px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
          }}
        />
        <button
          onClick={handleSave}
          disabled={!presetName.trim()}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#fff',
            backgroundColor: presetName.trim() ? '#28a745' : '#ccc',
            border: 'none',
            borderRadius: '4px',
            cursor: presetName.trim() ? 'pointer' : 'default',
          }}
        >
          Save
        </button>
      </div>

      {error && (
        <div style={{ padding: '6px 8px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '11px' }}>
          {error}
        </div>
      )}

      {/* Preset list */}
      {presets.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>No saved presets</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {presets.map((p) => (
            <div
              key={p.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </div>
                <div style={{ fontSize: '10px', color: '#888' }}>{formatDate(p.date)}</div>
              </div>
              {confirmLoad === p.name ? (
                <>
                  <span style={{ fontSize: '10px', color: '#856404' }}>Replace config?</span>
                  <button onClick={() => handleLoad(p.name)} style={smallBtnStyle('#007bff')}>Yes</button>
                  <button onClick={() => setConfirmLoad(null)} style={smallBtnStyle('#6c757d')}>No</button>
                </>
              ) : (
                <>
                  <button onClick={() => setConfirmLoad(p.name)} style={smallBtnStyle('#007bff')}>Load</button>
                  <button onClick={() => handleDelete(p.name)} style={smallBtnStyle('#dc3545')}>Del</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function smallBtnStyle(bg: string): React.CSSProperties {
  return {
    padding: '3px 8px',
    fontSize: '10px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: bg,
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}
